import { useEffect, useRef, useState } from 'react'
import { t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Raster } from './Zeitraum'

/**
 * Wie die Zeitreihe eines Berichts gelesen wird — **einzeln oder kumuliert**
 * (7.3 Abschnitt 2a, R426–R428).
 *
 * **Eine Achse, zwei Vokabulare.** Der Konzernabschluss kennt dieselbe Frage
 * unter zwei Namen, je nachdem, worauf sie gestellt wird:
 *
 * | | einzeln | kumuliert |
 * |---|---|---|
 * | **Bestand** (Bilanz) | *Verkehrszahl* — was sich in der Spalte bewegt hat | *Saldo* — der Stand am Ende der Spalte |
 * | **Strom** (GuV, Kapitalflussrechnung) | *Monatswert* — was in der Spalte angefallen ist | *Kumuliert seit Jahresbeginn* — alles seit dem Geschäftsjahresanfang |
 *
 * Es ist derselbe Gedanke, zweimal: **Ein Bestand ist von Natur aus die
 * kumulierte Größe** und seine Veränderung die einzelne; bei einem Strom ist
 * es umgekehrt. Deshalb ist es ein Feld und nicht zwei, und deshalb heißt es
 * in jedem Bericht so, wie der Anwender dort spricht — „YTD in der Bilanz"
 * sagt niemand.
 *
 * **Der Vorgabewert ist je Familie der natürliche:** die Bilanz öffnet auf
 * *Saldo*, die GuV auf *Monatswert*. Weil die beiden verschieden sind, merkt
 * sich die Anwendung **zwei Wahlen und nicht eine** — sonst müsste ein
 * Bildschirm falsch aufmachen, damit der andere richtig aufmacht.
 *
 * **Und das Feld steht nur, wo es wirkt** (R293): im Spaltenbild *Perioden*.
 * Eine einzelne Spalte hat keine Zeitreihe, in der sich etwas kumulieren
 * ließe; ein Feld, das dort steht und nichts tut, schickt den Anwender auf die
 * Suche nach seinem eigenen Fehler.
 *
 * **Bis zum 2026-09-13 stand hier ein anderes Feld** — *Anzeige* mit dem
 * Haken *Bewegungsbilanz*, geschlossen beschriftet mit „Beträge". Der
 * Auftraggeber hat es abgelöst: *„Aus dem Auswahlfeld ‚Beträge‘ machen wir in
 * der Bilanz ‚Saldo/Verkehrszahl‘. In der GuV und KFR soll die Auswahl
 * ‚Monatswert/YTD‘ benannt sein."* Was es besser macht, ist nicht der Name
 * allein: Ein Haken hat einen unbenannten Gegenzustand — „ohne Haken" heißt
 * *Saldo*, aber das steht nirgends. **Zwei benannte Werte haben keinen
 * unbenannten Zustand**, und das zählt doppelt, sobald ein Drittwerkzeug die
 * Ansicht abfragt (R428).
 *
 * **Seit dem 2026-09-17 steht das Wort ausgeschrieben da** — *Kumuliert seit
 * Jahresbeginn* statt *YTD*, so wie es die Stammdaten ohnehin schon führten
 * (`stammdaten.wert.ytd`). Das Zitat oben bleibt, wie es gesprochen wurde; die
 * Abkürzung steht heute nur noch in der Erläuterung, wo sie erklärt und nicht
 * beschriftet.
 */
export type Zeitwert = 'einzeln' | 'kumuliert'

/** Worauf die Wahl gestellt wird — ein Bestand oder ein Strom. */
export type Zeitwertfamilie = 'bestand' | 'strom'

/** Der natürliche Vorgabewert je Familie. */
export const ZEITWERTVORGABE: Record<Zeitwertfamilie, Zeitwert> = {
  bestand: 'kumuliert',
  strom: 'einzeln',
}

/**
 * Wie das Feld und seine beiden Werte in dieser Familie heißen — in der
 * Reihenfolge, in der sie in der Liste stehen: der Vorgabewert zuerst.
 */
export function zeitwertvorrat(
  familie: Zeitwertfamilie,
  raster: Raster,
): { wert: Zeitwert; name: Sprachschluessel; erlaeuterung: Sprachschluessel }[] {
  return familie === 'bestand'
    ? [
        {
          wert: 'kumuliert',
          name: 'zeitwert.bestand.kumuliert',
          erlaeuterung: 'zeitwert.bestand.kumuliert.erlaeuterung',
        },
        {
          wert: 'einzeln',
          name: 'zeitwert.bestand.einzeln',
          erlaeuterung: 'zeitwert.bestand.einzeln.erlaeuterung',
        },
      ]
    : [
        {
          wert: 'einzeln',
          name: EINZELWERT[raster],
          erlaeuterung: 'zeitwert.strom.einzeln.erlaeuterung',
        },
        {
          wert: 'kumuliert',
          name: 'zeitwert.strom.kumuliert',
          erlaeuterung: 'zeitwert.strom.kumuliert.erlaeuterung',
        },
      ]
}

/**
 * **Der Einzelwert eines Stroms heißt nach dem Raster** (R430) — *Monatswert*,
 * *Quartalswert*, *Jahreswert*. Vom 2026-09-13 bis zur Nachbesserung am selben
 * Tag hieß er in jedem Raster „Monatswert": Über einer Quartalsspalte stand
 * damit ein Wort, das einen Zeitraum behauptete, den die Spalte nicht trägt —
 * derselbe Fehler, den R185 an der Spaltenüberschrift verbietet.
 *
 * **Der Feldname folgt dem nicht.** Er heißt weiter *Monatswert/Kumuliert* und ist
 * der Name der Wahl, nicht ihres Ergebnisses: Ein Feld, dessen Beschriftung
 * mitwandert, sobald jemand das Raster anfasst, wäre am Bildschirm nicht
 * wiederzufinden.
 */
const EINZELWERT: Record<Raster, Sprachschluessel> = {
  monat: 'zeitwert.strom.einzeln.monat',
  quartal: 'zeitwert.strom.einzeln.quartal',
  jahr: 'zeitwert.strom.einzeln.jahr',
}

/** Der Name des Feldes — er nennt beide Pole, damit die Wahl vor dem Öffnen klar ist. */
export function zeitwerttitel(familie: Zeitwertfamilie): Sprachschluessel {
  return familie === 'bestand' ? 'zeitwert.bestand.titel' : 'zeitwert.strom.titel'
}

/** Wie der gewählte Wert heißt — beim Strom nach dem Raster (R430). */
export function zeitwertname(
  familie: Zeitwertfamilie,
  wert: Zeitwert,
  raster: Raster,
): Sprachschluessel {
  return zeitwertvorrat(familie, raster).find((v) => v.wert === wert)?.name ?? zeitwerttitel(familie)
}

export function Zeitwertwahl({
  familie,
  raster,
  gewaehlt,
  aufWahl,
}: {
  familie: Zeitwertfamilie
  /** Das Raster der Kontextzeile — es benennt den Einzelwert eines Stroms (R430). */
  raster: Raster
  gewaehlt: Zeitwert
  aufWahl: (w: Zeitwert) => void
}) {
  const [offen, setOffen] = useState(false)
  const rahmen = useRef<HTMLDivElement>(null)
  const beschriftung = `zeitwert-${familie}`

  useEffect(() => {
    if (!offen) {
      return
    }

    function beiKlickAussen(e: MouseEvent) {
      if (!rahmen.current?.contains(e.target as Node)) {
        setOffen(false)
      }
    }

    function beiTaste(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOffen(false)
      }
    }

    window.addEventListener('mousedown', beiKlickAussen)
    window.addEventListener('keydown', beiTaste)
    return () => {
      window.removeEventListener('mousedown', beiKlickAussen)
      window.removeEventListener('keydown', beiTaste)
    }
  }, [offen])

  return (
    <div className="feld zusatzfeld" ref={rahmen}>
      <label id={beschriftung}>{t(zeitwerttitel(familie))}</label>
      <button
        type="button"
        className="zusatzknopf"
        aria-expanded={offen}
        aria-haspopup="true"
        aria-labelledby={beschriftung}
        onClick={() => setOffen(!offen)}
      >
        <span>{t(zeitwertname(familie, gewaehlt, raster))}</span>
        <span aria-hidden="true">▾</span>
      </button>

      {offen && (
        /* Eine Wahl zwischen zweien ist ein Radiofeld und keine Hakenliste:
           Es gibt keinen Zustand „keines von beiden". */
        <div className="zusatzliste" role="radiogroup" aria-labelledby={beschriftung}>
          {zeitwertvorrat(familie, raster).map((v) => (
            <label key={v.wert}>
              <input
                type="radio"
                name={beschriftung}
                checked={gewaehlt === v.wert}
                onChange={() => {
                  aufWahl(v.wert)
                  setOffen(false)
                }}
              />
              <span>
                <span className="name">{t(v.name)}</span>
                <span className="erlaeuterung">{t(v.erlaeuterung)}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
